import { useCentralizedMe } from '@crehana/auth';
import { useSyncExternalStore } from 'react';

import { useForcedUi } from '@/shared/hooks/use-forced-ui';

import type { TMyOvertimeRequest } from '../../types';
import {
  getForcedMyRequestsVersion,
  resolveForcedMyRequests,
  subscribeToForcedMyRequests,
} from './forced-my-requests';
import { useTimeAndAttendanceUserMyOvertimeRequestsListQuery } from './list-my-overtime-requests.v5.generated';

type TUseMyRequestsParams = {
  page: number;
  pageSize: number;
};

type TUseMyRequestsResult = {
  items: TMyOvertimeRequest[];
  totalCount: number;
  loading: boolean;
  error: boolean;
  refetch: () => void;
};

// The gateway answers HTTP 200 with the error member inside `data`, so Apollo never sets
// `error` for a Forbidden response. Branch on `__typename`, never on `error` alone.
export const useMyRequests = ({
  page,
  pageSize,
}: TUseMyRequestsParams): TUseMyRequestsResult => {
  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization?.id ?? 0;

  // QA/demo override, off unless `?force=solicitudes[:variant]` is in the
  // URL. `useSyncExternalStore` re-renders this hook when a forced cancel
  // drops a row (see forced-my-requests.ts).
  useSyncExternalStore(
    subscribeToForcedMyRequests,
    getForcedMyRequestsVersion,
    getForcedMyRequestsVersion,
  );
  const forced = resolveForcedMyRequests(
    useForcedUi('solicitudes'),
    page,
    pageSize,
  );

  const { data, loading, error, refetch } =
    useTimeAndAttendanceUserMyOvertimeRequestsListQuery({
      variables: {
        input: {
          centralized_organization_id: orgId,
          pagination: { page, page_size: pageSize },
        },
      },
      skip: forced !== undefined || !orgId,
      context: { clientName: 'v5' },
      fetchPolicy: 'cache-and-network',
    });

  const refetchVoid = () => {
    refetch().catch(() => undefined);
  };

  if (forced !== undefined) {
    return {
      items: forced.items,
      totalCount: forced.totalCount,
      loading: forced.loading,
      error: forced.error,
      refetch: () => undefined,
    };
  }

  const result = data?.time.overtime_requests.list_my_overtime_requests;
  const isPayload = result?.__typename === 'TimeListMyOvertimeRequestsPayload';
  const hasBackendError = Boolean(result) && !isPayload;

  return {
    items: isPayload ? result.overtime_requests : [],
    totalCount: isPayload ? result.total_count : 0,
    loading,
    error: Boolean(error) || hasBackendError,
    refetch: refetchVoid,
  };
};
